/**
 * ClassInsightsCard — Retro vs Development snapshot at the top of Analytics.
 *
 * Renders a compact 2-column card (Retro | Development) plus a Combined
 * footer strip so leadership sees the P&L split without leaving the page.
 * Data comes from `GET /api/analytics/class-summary` which aggregates:
 *
 *   - installed_kwp    (kWp of live/handed-over sites)
 *   - pipeline_kwp     (kWp of every non-terminal in-flight project)
 *   - ytd_kwh          (actual generation since Jan 1 UTC)
 *   - ytd_revenue_gbp  (revenue since Jan 1 UTC across live sites)
 *
 * The card also renders a horizontal capacity meter that shows the Live
 * (installed) vs Pipeline split for each class so the "how much of our
 * book is generating today" question is answered visually at a glance.
 */
import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../../lib/api";
import { GBP, fmtDateGB } from "../../lib/format";
import { Zap, Building2, X, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "../../components/ui/popover";
import { CLASSIFICATIONS, CLASS_KEYS } from "../../lib/classification";
import { ResponsiveContainer, LineChart, Line, Tooltip } from "recharts";

const fmtKwp = (v) => `${(v || 0).toLocaleString("en-GB", { maximumFractionDigits: 1 })} kWp`;
const fmtKwh = (v) => {
  const n = v || 0;
  if (n >= 1e6) return `${(n / 1e6).toFixed(2)} GWh`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)} MWh`;
  return `${n.toFixed(0)} kWh`;
};

function CapacityMeter({ live, pipeline, accent }) {
  const total = (live || 0) + (pipeline || 0);
  if (total <= 0) {
    return (
      <div className="mt-2 h-1.5 rounded-full bg-[var(--line)]" title="No capacity yet" />
    );
  }
  const livePct = (live / total) * 100;
  return (
    <div className="mt-2">
      <div className="h-1.5 rounded-full overflow-hidden flex bg-[var(--line)]">
        {livePct > 0 && <div style={{ width: `${livePct}%`, background: accent }} />}
      </div>
      <div className="flex justify-between text-[9px] mt-1 font-mono muted">
        <span>Live {Math.round(livePct)}%</span>
        <span>Pipeline {Math.round(100 - livePct)}%</span>
      </div>
    </div>
  );
}

function YoyBadge({ pct, prev, testid }) {
  // pct === null → new revenue, no prior baseline
  // pct ===  0   → both YTD periods £0 (or exactly flat)
  // pct === undefined → backend didn't send the field (backwards compat)
  if (pct === undefined) return null;
  if (pct === null) {
    return (
      <span className="inline-flex items-center gap-0.5 text-[9px] font-mono font-semibold px-1.5 py-0.5 rounded bg-[var(--accent-soft,#DDF3F1)] text-[var(--accent-hover)] ml-1"
            data-testid={testid} title="No revenue in the prior year — first-year comparison unavailable">
        <TrendingUp size={9} /> NEW
      </span>
    );
  }
  const isFlat = Math.abs(pct) < 0.05;
  if (isFlat) {
    return (
      <span className="inline-flex items-center gap-0.5 text-[9px] font-mono font-semibold px-1.5 py-0.5 rounded bg-[var(--surface-2,#F5F5F4)] muted ml-1"
            data-testid={testid} title="Flat vs same YTD last year">
        <Minus size={9} /> —
      </span>
    );
  }
  const up = pct > 0;
  const color = up ? "#0F8B84" : "#B8321F";
  const bg = up ? "rgba(15,139,132,0.12)" : "rgba(184,50,31,0.12)";
  return (
    <span
      className="inline-flex items-center gap-0.5 text-[9px] font-mono font-semibold px-1.5 py-0.5 rounded ml-1"
      style={{ color, background: bg }}
      data-testid={testid}
      title={`vs same YTD last year (${prev != null ? GBP.format(prev) : "—"})`}
    >
      {up ? <TrendingUp size={9} /> : <TrendingDown size={9} />}
      {up ? "+" : ""}{pct.toFixed(1)}%
    </span>
  );
}

function TrendSparkline({ monthly, accent, testid }) {
  if (!monthly || !monthly.length) return null;
  const anyData = monthly.some(m => (m.actual_kwh || 0) > 0);
  return (
    <div className="mt-3" data-testid={testid}>
      <div className="flex items-center justify-between mb-1">
        <div className="text-[9px] uppercase tracking-wider muted font-semibold">
          12-month trend
        </div>
        <div className="text-[9px] muted font-mono">
          {monthly[0].month} → {monthly[monthly.length - 1].month}
        </div>
      </div>
      {anyData ? (
        <div style={{ height: 32 }} data-testid={`${testid}-chart`}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={monthly} margin={{ top: 2, right: 2, left: 2, bottom: 2 }}>
              <Tooltip
                cursor={false}
                contentStyle={{ fontSize: 10, padding: "4px 8px", borderRadius: 4, border: `1px solid ${accent}55` }}
                labelStyle={{ fontSize: 10, color: "#666" }}
                formatter={(v, name) => name === "actual_kwh"
                  ? [`${Math.round(v).toLocaleString("en-GB")} kWh`, "Generation"]
                  : [v, name]}
                labelFormatter={(m) => `Month · ${m}`}
              />
              <Line
                type="monotone"
                dataKey="actual_kwh"
                stroke={accent}
                strokeWidth={1.5}
                dot={false}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <div className="h-8 flex items-center justify-center text-[9px] muted italic"
             data-testid={`${testid}-empty`}>
          No generation data in the last 12 months
        </div>
      )}
    </div>
  );
}

/**
 * HealthRing — coloured SVG ring representing the 0-100 health score
 * for a class. Colour lives on a green→amber→red gradient so leadership
 * can eyeball momentum without reading the number.
 *
 *   ≥ 70 → green   (#0F8B84)
 *   40-69 → amber  (#B8721E)
 *   < 40  → red    (#B8321F)
 */
function HealthRing({ score, size = 44, testid, components, className = "" }) {
  if (score === undefined || score === null) return null;
  const pct = Math.max(0, Math.min(100, score));
  const stroke = 4;
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ * (1 - pct / 100);
  const colour = pct >= 70 ? "#0F8B84" : pct >= 40 ? "#B8721E" : "#B8321F";
  const band = pct >= 70 ? "Healthy" : pct >= 40 ? "Watch" : "At-risk";
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`relative rounded-full transition-shadow hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 ${className}`}
          style={{ width: size, height: size, ["--tw-ring-color"]: colour }}
          data-testid={testid}
          aria-label={`Class health ${pct.toFixed(0)} out of 100 — ${band}`}
        >
          <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
            <circle cx={size / 2} cy={size / 2} r={r}
                    stroke="var(--line)" strokeWidth={stroke} fill="none" />
            <circle cx={size / 2} cy={size / 2} r={r}
                    stroke={colour} strokeWidth={stroke} fill="none"
                    strokeLinecap="round"
                    strokeDasharray={circ}
                    strokeDashoffset={offset}
                    transform={`rotate(-90 ${size / 2} ${size / 2})`}
                    style={{ transition: "stroke-dashoffset 0.6s ease" }} />
            <text x="50%" y="52%" textAnchor="middle" dominantBaseline="middle"
                  fontSize="11" fontWeight="700" fill={colour}
                  fontFamily="ui-monospace, monospace">
              {Math.round(pct)}
            </text>
          </svg>
        </button>
      </PopoverTrigger>
      <PopoverContent side="bottom" align="end" collisionPadding={12} className="w-64 p-3"
                      data-testid={`${testid}-popover`}>
        <div className="flex items-baseline justify-between mb-2">
          <div className="text-[10px] uppercase tracking-[0.14em] font-semibold muted">Class health</div>
          <span className="text-[10px] font-mono font-semibold px-1.5 py-0.5 rounded"
                style={{ background: `${colour}22`, color: colour }}>
            {band}
          </span>
        </div>
        <div className="flex items-baseline gap-1 mb-3">
          <span className="font-display text-2xl font-bold" style={{ color: colour }}>
            {Math.round(pct)}
          </span>
          <span className="text-xs muted">/ 100</span>
        </div>
        {components && (
          <div className="space-y-1.5 text-xs font-mono">
            <HealthRow label="YoY momentum"    weight="40%" value={components.yoy_component} />
            <HealthRow label="Live / pipeline" weight="30%" value={components.live_ratio_component} />
            <HealthRow label="Revenue / kWp"   weight="30%" value={components.revenue_per_kwp_component}
                       hint={`£${components.revenue_per_kwp_gbp}/kWp`} />
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

function HealthRow({ label, weight, value, hint }) {
  const pct = Math.max(0, Math.min(100, Number(value) || 0));
  const barColour = pct >= 70 ? "#0F8B84" : pct >= 40 ? "#B8721E" : "#B8321F";
  return (
    <div>
      <div className="flex items-baseline justify-between text-[11px]">
        <span className="text-[var(--ink)]">
          {label} <span className="muted font-normal">· {weight}</span>
        </span>
        <span className="font-semibold" style={{ color: barColour }}>
          {Math.round(pct)}
          {hint && <span className="ml-1 muted text-[10px] font-normal">({hint})</span>}
        </span>
      </div>
      <div className="h-1 rounded-full bg-[var(--line)] mt-0.5 overflow-hidden">
        <div style={{ width: `${pct}%`, background: barColour, height: "100%" }} />
      </div>
    </div>
  );
}

function ClassColumn({ label, sub, tone, accent, data, icon: Icon, testid, classKey, onDrillIn,
                       pageFilter, onSetPageFilter }) {
  const b = data || {};
  const isFilterActive = pageFilter === classKey;
  return (
    <div
      className={`p-4 rounded-lg border bg-white transition-shadow ${isFilterActive ? "shadow-md" : ""}`}
      style={{ borderColor: isFilterActive ? accent : `${accent}55`, borderWidth: isFilterActive ? 2 : 1 }}
      data-testid={testid}
    >
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <Icon size={14} style={{ color: accent }} />
          <div>
            <div className="text-[10px] uppercase tracking-[0.16em] font-semibold" style={{ color: accent }}>
              {label}
            </div>
            <div className="text-[10px] muted">{sub}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <HealthRing
            score={b.health_score}
            components={b.health_components}
            testid={`${testid}-health-ring`}
          />
          {onSetPageFilter && classKey && classKey !== "combined" && (
          <button
            type="button"
            onClick={() => onSetPageFilter(isFilterActive ? null : classKey)}
            className={`text-[9px] uppercase tracking-wider font-mono font-semibold px-2 py-0.5 rounded transition-colors ${
              isFilterActive
                ? "text-white"
                : "hover:bg-[var(--surface-2,#F5F5F4)]"
            }`}
            style={isFilterActive ? { background: accent } : { color: accent, border: `1px solid ${accent}55` }}
            data-testid={`${testid}-filter-toggle`}
            title={isFilterActive
              ? `Currently filtering Analytics to ${label} — click to clear`
              : `Filter the whole page to ${label} sites only`}
          >
            {isFilterActive ? "✓ Filtering" : "Filter page"}
          </button>
        )}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-wider muted font-semibold">Installed</div>
          <div className="font-mono text-xl font-bold" style={{ color: accent }}
               data-testid={`${testid}-installed-kwp`}>
            {fmtKwp(b.installed_kwp)}
          </div>
          <div className="text-[10px] muted">
            {b.live_site_count || 0} live site{b.live_site_count === 1 ? "" : "s"}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider muted font-semibold">Pipeline</div>
          <div className="font-mono text-xl font-bold text-[var(--ink)]"
               data-testid={`${testid}-pipeline-kwp`}>
            {fmtKwp(b.pipeline_kwp)}
          </div>
          <div className="text-[10px] muted">
            {b.pipeline_site_count || 0} project{b.pipeline_site_count === 1 ? "" : "s"}
          </div>
        </div>
      </div>
      <CapacityMeter live={b.installed_kwp} pipeline={b.pipeline_kwp} accent={accent} />
      <div className="grid grid-cols-2 gap-3 mt-4 pt-3 border-t border-[var(--line)]">
        <div>
          <div className="text-[10px] uppercase tracking-wider muted font-semibold">YTD gen.</div>
          <div className="font-mono text-sm font-semibold flex items-center gap-1"
               data-testid={`${testid}-ytd-kwh`}>
            <Zap size={11} style={{ color: accent }} />
            {fmtKwh(b.ytd_kwh)}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider muted font-semibold">YTD revenue</div>
          <div className="flex items-baseline flex-wrap gap-1">
            {onDrillIn && classKey && classKey !== "combined" ? (
              <button
                type="button"
                onClick={() => onDrillIn(classKey)}
                className="font-mono text-sm font-semibold underline decoration-dotted underline-offset-2 hover:opacity-80 transition-opacity text-left"
                style={{ color: accent }}
                data-testid={`${testid}-ytd-revenue`}
                title="Click for per-site P&L breakdown"
              >
                {GBP.format(b.ytd_revenue_gbp || 0)} <span className="text-[10px]">↗</span>
              </button>
            ) : (
              <div className="font-mono text-sm font-semibold"
                   data-testid={`${testid}-ytd-revenue`}>
                {GBP.format(b.ytd_revenue_gbp || 0)}
              </div>
            )}
            <YoyBadge
              pct={b.yoy_delta_pct}
              prev={b.prev_ytd_revenue_gbp}
              testid={`${testid}-yoy-badge`}
            />
          </div>
        </div>
      </div>
      <TrendSparkline monthly={b.monthly} accent={accent} testid={`${testid}-sparkline`} />
    </div>
  );
}

function ClassPnlModal({ classKey, onClose }) {
  const [pnl, setPnl] = useState(null);
  const [err, setErr] = useState(null);
  // Look up label + accent from the shared classification catalogue so
  // adding a class is a one-file edit in lib/classification.js.
  const meta = CLASSIFICATIONS.find(c => c.key === classKey) || CLASSIFICATIONS[0];
  const { label, tone: accent } = meta;

  useEffect(() => {
    let mounted = true;
    setPnl(null); setErr(null);
    api.get(`/analytics/portfolio-pnl?range=365d&class=${classKey}`)
      .then(({ data }) => { if (mounted) setPnl(data); })
      .catch((e) => { if (mounted) setErr(e?.response?.data?.detail || "Failed to load per-site P&L"); });
    return () => { mounted = false; };
  }, [classKey]);

  return (
    <div className="modal-overlay" onClick={onClose} data-testid="class-pnl-modal">
      <div className="modal-card max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.16em] font-semibold" style={{ color: accent }}>
              {label} · trailing 12 months
            </div>
            <h2 className="font-display text-xl font-bold mt-1">Per-site P&amp;L breakdown</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded hover:bg-[var(--bg-2)]"
            data-testid="class-pnl-modal-close"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>
        {err && (
          <div className="p-4 text-xs text-[var(--err)] bg-[var(--err-soft,#FEE)] rounded border border-[var(--err)]"
               data-testid="class-pnl-modal-error">
            {err}
          </div>
        )}
        {!err && !pnl && (
          <div className="p-8 text-center muted text-xs" data-testid="class-pnl-modal-loading">
            Loading…
          </div>
        )}
        {pnl && (
          <>
            <div className="grid grid-cols-4 gap-3 mb-4">
              <div className="p-3 rounded border border-[var(--line)]">
                <div className="text-[10px] uppercase tracking-wider muted font-semibold">Sites</div>
                <div className="font-display text-xl font-bold" data-testid="class-pnl-modal-sites">
                  {pnl.summary.site_count}
                </div>
              </div>
              <div className="p-3 rounded border border-[var(--line)]">
                <div className="text-[10px] uppercase tracking-wider muted font-semibold">Revenue</div>
                <div className="font-display text-xl font-bold" style={{ color: accent }}
                     data-testid="class-pnl-modal-revenue">
                  {GBP.format(pnl.summary.revenue_gbp)}
                </div>
              </div>
              <div className="p-3 rounded border border-[var(--line)]">
                <div className="text-[10px] uppercase tracking-wider muted font-semibold">OpEx</div>
                <div className="font-display text-xl font-bold text-[var(--ink)]">
                  {GBP.format(pnl.summary.opex_gbp)}
                </div>
              </div>
              <div className="p-3 rounded border border-[var(--line)]">
                <div className="text-[10px] uppercase tracking-wider muted font-semibold">Net</div>
                <div className="font-display text-xl font-bold"
                     style={{ color: pnl.summary.net_gbp >= 0 ? "var(--accent-hover)" : "var(--err,#D64545)" }}
                     data-testid="class-pnl-modal-net">
                  {GBP.format(pnl.summary.net_gbp)}
                </div>
              </div>
            </div>
            {pnl.per_site.length === 0 ? (
              <div className="p-6 text-center muted text-xs italic" data-testid="class-pnl-modal-empty">
                No sites in this classification yet.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="data-table w-full text-sm" data-testid="class-pnl-modal-table">
                  <thead>
                    <tr>
                      <th className="text-left">Site</th>
                      <th className="text-right">Revenue</th>
                      <th className="text-right">OpEx</th>
                      <th className="text-right">Net</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pnl.per_site.map(s => (
                      <tr key={s.project_id} data-testid={`class-pnl-row-${s.project_id}`}>
                        <td className="text-left">{s.project_name}</td>
                        <td className="text-right font-mono">{GBP.format(s.revenue_gbp)}</td>
                        <td className="text-right font-mono muted">{GBP.format(s.opex_gbp)}</td>
                        <td className="text-right font-mono font-semibold"
                            style={{ color: s.net_gbp >= 0 ? "var(--accent-hover)" : "var(--err,#D64545)" }}>
                          {GBP.format(s.net_gbp)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default function ClassInsightsCard({ pageFilter, onSetPageFilter } = {}) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const drillInClass = searchParams.get("class");
  const openDrillIn = (cls) => setSearchParams((prev) => {
    const next = new URLSearchParams(prev);
    next.set("class", cls);
    return next;
  }, { replace: false });
  const closeDrillIn = () => setSearchParams((prev) => {
    const next = new URLSearchParams(prev);
    next.delete("class");
    return next;
  }, { replace: false });

  useEffect(() => {
    let mounted = true;
    api.get("/analytics/class-summary")
      .then(({ data }) => { if (mounted) setData(data); })
      .catch(() => { if (mounted) setErr(true); });
    return () => { mounted = false; };
  }, []);

  if (err) return null; // Silently drop the card — not a blocker for the page.
  if (!data) {
    return (
      <div className="mb-6 p-4 rounded-lg border border-[var(--line)] bg-[var(--surface-2)] text-xs muted"
           data-testid="class-insights-loading">
        Loading class insights…
      </div>
    );
  }
  const c = data.classes || {};
  return (
    <section className="mb-6" data-testid="class-insights-card">
      <div className="flex items-baseline justify-between mb-2">
        <div>
          <div className="label">{CLASSIFICATIONS.map(c => c.label).join(" · ")}</div>
          <h2 className="font-display text-lg font-bold">Class insights · YTD {data.year}</h2>
        </div>
        <div className="text-[10px] muted font-mono">since {fmtDateGB(data.ytd_start)}</div>
      </div>
      {/* Grid stays symmetric — 1 col on mobile, 2 cols mid, and one col per
          class on wide. Keeps a 4th class centred if we ever add one. */}
      <div
        className="grid gap-3"
        style={{ gridTemplateColumns: `repeat(auto-fit, minmax(240px, 1fr))` }}
      >
        {CLASSIFICATIONS.map(cls => (
          <ClassColumn
            key={cls.key}
            label={cls.label}
            sub={cls.sub}
            accent={cls.tone}
            data={c[cls.key]}
            icon={cls.icon}
            testid={`class-insights-${cls.key}`}
            classKey={cls.key}
            onDrillIn={openDrillIn}
            pageFilter={pageFilter}
            onSetPageFilter={onSetPageFilter}
          />
        ))}
      </div>
      {c.combined && (
        <div className="mt-3 p-3 rounded-lg border border-[var(--line)] bg-[var(--surface-2)] flex flex-wrap items-center justify-between gap-4"
             data-testid="class-insights-combined">
          <div className="flex items-center gap-2">
            <Building2 size={12} className="muted" />
            <div className="text-[10px] uppercase tracking-[0.16em] font-semibold muted">Combined book</div>
          </div>
          <div className="flex flex-wrap items-baseline gap-x-6 gap-y-1 font-mono text-sm">
            <div>
              <span className="text-[10px] uppercase tracking-wider muted mr-1">Installed</span>
              <span className="font-semibold" data-testid="class-insights-combined-installed">
                {fmtKwp(c.combined.installed_kwp)}
              </span>
            </div>
            <div>
              <span className="text-[10px] uppercase tracking-wider muted mr-1">Pipeline</span>
              <span className="font-semibold" data-testid="class-insights-combined-pipeline">
                {fmtKwp(c.combined.pipeline_kwp)}
              </span>
            </div>
            <div>
              <span className="text-[10px] uppercase tracking-wider muted mr-1">YTD gen</span>
              <span className="font-semibold" data-testid="class-insights-combined-kwh">
                {fmtKwh(c.combined.ytd_kwh)}
              </span>
            </div>
            <div>
              <span className="text-[10px] uppercase tracking-wider muted mr-1">YTD rev</span>
              <span className="font-semibold" data-testid="class-insights-combined-revenue">
                {GBP.format(c.combined.ytd_revenue_gbp || 0)}
              </span>
              <YoyBadge
                pct={c.combined.yoy_delta_pct}
                prev={c.combined.prev_ytd_revenue_gbp}
                testid="class-insights-combined-yoy-badge"
              />
            </div>
          </div>
        </div>
      )}
      {drillInClass && CLASS_KEYS.includes(drillInClass) && (
        <ClassPnlModal classKey={drillInClass} onClose={closeDrillIn} />
      )}
    </section>
  );
}
